import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

import './_profileHolder.scss'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { addToast } from 'components/Toast/Toast'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import FriendRequestModal from 'components/UI/Modal/FriendRequest/FriendRequestModal'
import ProfileImageModal from 'components/UI/Modal/ProfileImage/ProfileImageModal'
import GuestSubscribeLogin from 'components/UI/Modal/profileModals/GuestSubscribeLogin/GuestSubscribeLogin'
import SubscribeProfileModal from 'components/UI/Modal/profileModals/SubscribeProfileModal/SubscribeProfileModal'
import UnsubscribeProfileModal from 'components/UI/Modal/profileModals/UnsubscribeProfileModal/UnsubscribeProfileModal'
import { StoriesList } from 'components-ref/organisms'
import { useModalContext } from 'context/modalContext'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import { AllIcons } from 'helpers/allIcons'
import { useUserStories } from 'helpers/hooks'
import { Icons } from 'helpers/icons'
import { subscribeToUser } from 'services/endpoints/profile'
import { setSubscriptionRenewal } from 'services/endpoints/settings/general/subscription'
import { useIsLoggingOut, useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { type SubscribePayload } from 'types/api/profile'
import { type IProfile } from 'types/interfaces/IProfile'
import { type IProfileHolder } from 'types/interfaces/IProfileHolder'

import { AvatarFullscreenModal } from './components/AvatarFullscreenModal'
import { ProfileCover } from './components/ProfileCover'
import { ProfileInfo } from './components/ProfileInfo'

export const ProfileHolder: FC<IProfileHolder> = ({
  id,
  online,
  active_stream,
  role,
  cover,
  avatar,
  display_name,
  follower_count,
  friend_count,
  username,
  show_stories,
  friend_button,
  refetchProfileData,
  isSubscribed,
  photo_count,
  premium_video_count,
  video_count,
  friends_status,
  friendships,
  has_stories,
  stream_url,
  hideCount,
  mini_bio,
  subscribeModalOpen,
  setSubscribeModalOpen,
  show_follower_count,
  public_friends,
  show_likes_count,
  show_mini_bio,
  post_like_count,
  show_online_status,
  status,
  tab,
  rebill,
  updated_at,
  expiry_at,
  overall_revenue,
  ownProfile,
  fromDiscoverProfilePanel,
  discoverShowFeed = true,
  discoverProfileActive = true,
  subscription_plans,
  promo_campaign,
}: IProfileHolder) => {
  const modalData = useModalContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // const { refetchUserProfile } = useRefetchUserProfile()
  const getProfileFromCache = useProfileCacheStore(state => state.getProfile)
  const setProfileInCache  = useProfileCacheStore(state => state.setProfile)
  const navigate = useNavigate()
  const location = useLocation()

  const skipProfileStoriesRefetch =
    ownProfile === true &&
    location.state?.skipProfileStoriesRefetch === true
  const currentUser = useUserProfile()
  const isAuthenticated = !!currentUser?.id
  const viewerId = currentUser?.id ?? null
  // const storiesStaleTime = ownProfile ? 5 * 240_000 : 60_000
  const cachedProfile = username ? getProfileFromCache(username, viewerId) : undefined
  // Pick monthly (month_count === 1) plan id from cached profile; fallback to first plan.
  const defaultMonthlyPlanId = useMemo(() => {
    const plans = subscription_plans ?? []
    const monthly = plans.find(plan => Number(plan.month_count) === 1)

    return monthly?.id ?? plans[0]?.id ?? null
  }, [subscription_plans])

const [selectedSubscriptionPlan, setSelectedSubscriptionPLan] =
  useState<string | null>(
    defaultMonthlyPlanId ?? null
  )
const renewalMutation = useMutation(
  async (enableRenewal: boolean) => {
    return await setSubscriptionRenewal(id, enableRenewal)
  },
  {
    onSuccess: () => {
      if (username) markSubscribedLocally(username)
      refetchProfileData()
      setSubscribeModalOpen(false)
    },
    onError: () => {
      refetchProfileData()
      setSubscribeModalOpen(false)
      addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
    },
  }
)

function markSubscribedLocally(username: string) {
  const key = ['profile', username] as const

  // 1) React Query cache
  const prev = queryClient.getQueryData<IProfile>(key)
  if (prev) {
    const detail = Array.isArray(prev.subscription_detail)
      ? prev.subscription_detail
      : []

    const first = detail[0] ?? {}

    const next: IProfile = {
      ...prev,
      isSubscribed: true,
      subscription_detail: [{
        ...first,
        rebill: 1, // <-- number, not boolean
        updated_at: new Date().toISOString(),
      }],
    }

    queryClient.setQueryData<IProfile>(key, next)
  }

  // 2) Viewer-scoped Zustand cache
  const cached = getProfileFromCache(username, viewerId)
  if (cached) {
    const detail = Array.isArray(cached.subscription_detail)
      ? cached.subscription_detail
      : []

    const first = detail[0] ?? {}

    const nextCached: IProfile = {
      ...cached,
      isSubscribed: true,
      subscription_detail: [{
        ...first,
        rebill: 1, // <-- keep types consistent here too
        updated_at: new Date().toISOString(),
      }],
    }

    setProfileInCache(username, nextCached, viewerId)
  }
}


  // If the cache arrives after mount and we had no valid selection, set it once.
useEffect(() => {
  if (selectedSubscriptionPlan == null && defaultMonthlyPlanId) {
    setSelectedSubscriptionPLan(defaultMonthlyPlanId)
  }
}, [defaultMonthlyPlanId, selectedSubscriptionPlan])

  const isFriend = friends_status === 'Friends'
  const normalSubscribed = isSubscribed || isFriend

  const subscribed =
    fromDiscoverProfilePanel === true
      ? discoverShowFeed || normalSubscribed
      : normalSubscribed

  const [campaign, setCampaign] = useState<string | null>(null)

  const [avatarOpen, setAvatarOpen] = useState<boolean>(false)
  const normalCanSeeStories = ownProfile === true || subscribed

    const canSeeStories =
      fromDiscoverProfilePanel === true
        ? discoverProfileActive && subscribed
        : normalCanSeeStories

  const {
    stories,
    isLoading: storiesLoading,
    isFetched: storiesFetched,
  } = useUserStories({
    creatorId: id,
    enabled: canSeeStories,
    refetchOnMount: !skipProfileStoriesRefetch
  })

    function openAvatarPicture() {
      setAvatarOpen(true)
    }
    function onClose() {
      setAvatarOpen(false)
    }
    const changeAvatar = () => {
      modalData.addModal(
        t('profilePhoto'),
        <ProfileImageModal />,
        undefined,
        true,
        'profile__photo__modal'
      )
    }
    const changeCover = () => {
      modalData.addModal(t('coverImage'), <ProfileImageModal isCover={true} />)
    }

    const sendRequest = () => {
      modalData.addModal(
        t('FriendRequest'),
        <FriendRequestModal
          id={id}
          online={!!(online && show_online_status)}
          avatar={avatar}
          // friendships={friendships}
          display_name={display_name}
          username={username}
          follower_count={follower_count}
          friend_count={friend_count}
          friends_status={friends_status}
          close={() => {
            modalData.clearModal()
          }}
        />
      )
    }


      
  const subscribeToModel = async (paymentMethod: {
    method: 'card' | 'deposit'
    cardId: string | undefined
    rebill?: boolean
    taxRate?: number
    taxRateFee?: number | null
    tax?: number
    tax_rate?: number | null
    promo_campaign_id?: string
    subscription_plan_id?: string
  }) => {
    const activePlanIdFromProfile =
      (cachedProfile as any)?.subscription_detail?.[0]
        ?.subscription_plan?.id ??
      (cachedProfile as any)?.subscription_detail?.[0]?.id

    const selectedPlanId =
      typeof selectedSubscriptionPlan === 'string' &&
      selectedSubscriptionPlan !== ''
        ? selectedSubscriptionPlan
        : undefined

    const modalPlanId = paymentMethod.subscription_plan_id

    const resolvedPlanId =
      modalPlanId ??
      activePlanIdFromProfile ??
      selectedPlanId

    if (!resolvedPlanId) {
      addToast(
        'error',
        t('error:anErrorOccuredPleaseTryAgain')
      )

      throw new Error(
        'Unable to resolve subscription_plan_id'
      )
    }

    const resolvedPromoId =
      paymentMethod.promo_campaign_id ??
      campaign ??
      undefined

    const isCard = paymentMethod.method === 'card'

    const taxValue = isCard
      ? paymentMethod.tax ??
        paymentMethod.taxRate ??
        0
      : 0

    const taxRateValue = isCard
      ? paymentMethod.tax_rate ??
        paymentMethod.taxRateFee ??
        null
      : null

    const subscribePayload: SubscribePayload = {
      id,
      subscription_plan_id: resolvedPlanId,
      promo_campaign_id: resolvedPromoId,
      payment_method: paymentMethod.method,
      suggested: tab,
      card_id: paymentMethod.cardId,
      rebill: paymentMethod.rebill,
      tax: taxValue,
      tax_rate: taxRateValue,
    }

    return await subscribeToUser(subscribePayload)
  }

const subscribeMutation = useMutation(
  async (paymentMethod: {
    method: 'card' | 'deposit'
    cardId: string | undefined
    rebill?: boolean
    taxRate?: any
    taxRateFee?: any
  }) => await subscribeToModel(paymentMethod),
  {
    onSuccess: () => {
      if (username) markSubscribedLocally(username)
      refetchProfileData()
      setSubscribeModalOpen(false)
    },
    onError: (error: any) => {
      refetchProfileData()
      setSubscribeModalOpen(false)

      const code =
        error?.response?.data?.code ??
        error?.response?.status

      const message =
        error?.response?.data?.error?.message ??
        error?.response?.data?.message ??
        t('error:anErrorOccuredPleaseTryAgain')

      if (code === 403 && message === 'Account status is restricted.') {
        modalData.addModal(
          '',
          <ConfirmModal
            body='Account is Restricted'
            confirmText='OK'
            confirmFn={() => {}}
          />,
          undefined,
          false,
          'confirmModal'
        )
        return
      }

      addToast('error', message)
    },
  }
)

useEffect(() => {
  if (!skipProfileStoriesRefetch) return

  const { skipProfileStoriesRefetch: _, ...remainingState } =
    location.state ?? {}

  navigate(
    {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    },
    {
      replace: true,
      state: remainingState
    }
  )
}, [skipProfileStoriesRefetch, location, navigate])


const profileStoriesCount =
  Array.isArray(stories)
    ? stories.length
    : 0

const hasFreshStories =
  profileStoriesCount > 0

const shouldShowStories =
  canSeeStories &&
  (
    storiesFetched
      ? hasFreshStories
      : has_stories === true
  )

  return (
    <div className="profile">
      <ProfileCover
        cover={cover}
        photo_count={photo_count}
        premium_video_count={premium_video_count}
        video_count={video_count}
        hideCount={hideCount}
      />
      <div
        className={`profileholder profileholder__profile${
          shouldShowStories
            ? ' profileholder-stories'
            : ''
        }`}
      >
        <ImgInCircle
          id={id}
          type='profile'
          hasLoader={!!avatar}
          hasCamera={true}
          cameraFn={changeAvatar}
          isLive={!!(active_stream)}
        >
          {avatar ? (
            <img
              src={avatar || ''}
              alt={t('profileAvatar')}
              onClick={() => {
                if (!ownProfile && active_stream) {
                  navigate(`/watch/${username}`, {
                    state: {
                      data: {
                        id,
                        username,
                        avatar,
                        active_stream,
                        stream_url,
                      },
                    },
                  })
                  return
                }

                openAvatarPicture()
              }}
            />
          ) : (
            <div className='profileholder__profile__placeholder'>
              <img src={AllIcons.user_placeholder} alt='Placeholder' />
            </div>
          )}
        </ImgInCircle>
        {ownProfile ? (
          <div
            className='iic__camera iic__camera__cover'
            onClick={() => {
              changeCover()
            }}
          >
            <img src={Icons.camera} alt={t('changeCover')} />
          </div>
        ) : (
          ''
        )}

        <ProfileInfo
          id={id}
          role={role}
          display_name={display_name}
          follower_count={follower_count}
          friend_count={friend_count}
          username={username}
          show_stories={show_stories}
          friend_button={friend_button}
          isSubscribed={isSubscribed}
          friendRequest={sendRequest}
          avatar={avatar ?? AllIcons.user_placeholder}
          friends_status={friends_status}
          setSubscribeModalOpen={setSubscribeModalOpen}
          mini_bio={mini_bio ?? ''}
          show_likes_count={show_likes_count}
          public_friends={public_friends}
          show_follower_count={show_follower_count}
          show_mini_bio={show_mini_bio}
          post_like_count={post_like_count}
          subscription_plans={subscription_plans}
          promo_campaign={promo_campaign}
          setCampaign={setCampaign}
          rebill={rebill}
          overall_revenue={overall_revenue}
          // Prefer cached resource values for instant render

        />

{shouldShowStories && (
  <>
    {storiesLoading && !hasFreshStories ? (
      <div className='profile-story-skeleton'>
        <div className='profile-story-skeleton__circle' />
        <div className='profile-story-skeleton__text' />
      </div>
    ) : hasFreshStories ? (
      <StoriesList
        id={id}
        stories={stories}
        page='profile'
        username={username}
        displayName={display_name}
        avatar={avatar}
      />
    ) : null}
  </>
)}
      </div>

      <ModalWrapper
        open={subscribeModalOpen}
        setOpen={setSubscribeModalOpen}
        customClass='subscribe-profile-modal-modal'
        hasCloseButton={!isSubscribed}
      >
        {isSubscribed ? (
          <UnsubscribeProfileModal
            expiry_at={expiry_at}
            avatar={avatar ?? AllIcons.user_placeholder}
            displayName={display_name}
            username={username}
            rebill={rebill}
            updated_at={updated_at}
            subscribeFn={() => renewalMutation.mutate(Number(rebill) !== 1)}
            isLoading={renewalMutation.isLoading}
            setOpen={setSubscribeModalOpen}
          />
        ) : !isAuthenticated ? (
          <GuestSubscribeLogin
            displayName={display_name}
            avatar={avatar ?? AllIcons.user_placeholder}
            username={username}
            cover={cover}
            onClose={() => setSubscribeModalOpen(false)}
          />
        ) : (
          <SubscribeProfileModal
            displayName={display_name}
            avatar={avatar ?? AllIcons.user_placeholder}
            username={username}
            id={id}
            subscribeFn={(paymentMethod: {
              method: 'card' | 'deposit'
              cardId: string | undefined
              rebill?: boolean
              taxRate?: any
              taxRateFee?: any
            }) => subscribeMutation.mutate(paymentMethod)}
            isLoading={subscribeMutation.isLoading}
            selected={selectedSubscriptionPlan ?? undefined}
            setSelected={setSelectedSubscriptionPLan}
            campaign={campaign}
            setCampaign={setCampaign}
            initialPlans={subscription_plans ?? []}
            initialCampaign={
              cachedProfile?.promo_campaign ??
              promo_campaign ??
              null
            }
          />
        )}
      </ModalWrapper>

      {avatarOpen && (
        <AvatarFullscreenModal
          avatar={avatar ?? ''}
          alt={t('profileAvatar')}
          onClose={onClose}
        />
      )}
    </div>
  )
}
